import { useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errorMessage } from "@/lib/api";

const EMPTY = { name: "", sebi_reg: "", email: "" };

export const RADialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState({ type: "idle", msg: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const close = (v) => {
    onOpenChange(v);
    if (!v) { setStatus({ type: "idle", msg: "" }); setForm(EMPTY); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return setStatus({ type: "error", msg: "Enter your full name" });
    if (form.sebi_reg.trim().length < 4) return setStatus({ type: "error", msg: "Enter your SEBI RA registration number" });
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return setStatus({ type: "error", msg: "Enter a valid email address" });
    setStatus({ type: "loading", msg: "" });
    try {
      await api.post("/ra-applications", form);
      setStatus({ type: "success", msg: "" });
      toast.success("RA partner application received");
    } catch (err) {
      setStatus({ type: "error", msg: errorMessage(err) });
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-[var(--tn-card)] border-[var(--tn-line)] text-[var(--tn-text)] sm:max-w-md" data-testid="ra-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Become an RA partner</DialogTitle>
          <DialogDescription className="text-[var(--tn-muted)]">
            Publish strategies to Trading Nifty subscribers. We verify every SEBI Research Analyst registration before listing.
          </DialogDescription>
        </DialogHeader>
        {status.type === "success" ? (
          <div data-testid="ra-success" className="flex gap-3 rounded-lg border border-[rgba(163,230,53,0.35)] bg-[rgba(163,230,53,0.06)] p-4">
            <CheckCircle2 className="text-[var(--tn-green)] shrink-0" size={20} />
            <p className="text-sm">Thanks — your application is in. Our partnerships desk will verify your registration and reach out by email.</p>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="space-y-4" data-testid="ra-form">
            <label className="block"><span className="text-xs text-[var(--tn-muted)]">Full name *</span>
              <input className="tn-input mt-1.5" value={form.name} onChange={set("name")} data-testid="ra-name-input" /></label>
            <label className="block"><span className="text-xs text-[var(--tn-muted)]">SEBI RA registration number *</span>
              <input className="tn-input mt-1.5 font-mono uppercase" placeholder="INH000000000" value={form.sebi_reg} onChange={set("sebi_reg")} data-testid="ra-sebi-input" /></label>
            <label className="block"><span className="text-xs text-[var(--tn-muted)]">Email address *</span>
              <input type="email" className="tn-input mt-1.5" value={form.email} onChange={set("email")} data-testid="ra-email-input" /></label>
            {status.type === "error" && <p className="text-sm text-[var(--tn-red)]" data-testid="ra-error">{status.msg}</p>}
            <button type="submit" className="tn-btn w-full h-11" disabled={status.type === "loading"} data-testid="ra-submit-btn">
              {status.type === "loading" && <Loader2 className="animate-spin" size={16} />} Submit application
            </button>
          </form>
        )}
        <p className="text-xs text-[var(--tn-muted)]">
          Not a registered RA?{" "}
          <a href="#waitlist" onClick={() => close(false)} className="text-[var(--tn-cyan)] hover:underline" data-testid="ra-join-waitlist-link">Join the waitlist instead</a>
        </p>
      </DialogContent>
    </Dialog>
  );
};
